/**
 * Blade and dialog open/close state for the site dashboard.
 *
 * Centralises the useState cluster that governs which drawers and modals
 * are visible on the site dashboard — RelayBlade, MaintenanceBlade,
 * CameraSchedulesBlade, CommandsBlade, SensorBlade, IntrusionBlade, and the
 * StructureCardConfigureDialog.
 * The view calls this hook and spreads the returned fields directly onto the
 * blade components.
 *
 * Contains:
 * - useDashboardBlades — hook (default export)
 */

import { useState } from 'react';

/**
 * Manages blade / dialog open state for the site dashboard.
 *
 * Returns plain state values and stable setter callbacks so the view
 * can stay focused on composition.
 *
 * @returns {Object} blade state and handlers
 */
export default function useDashboardBlades() {
  const [isRelayBladeOpen, setIsRelayBladeOpen] = useState(false);
  const [relayBladeParams, setRelayBladeParams] = useState({
    initialRelayId: null,
    initialScheduleId: null,
    initialTab: 'config',
  });
  const [commandsOpen, setCommandsOpen] = useState(false);
  const [configureDisplayOpen, setConfigureDisplayOpen] = useState(false);
  const [relocateOpen, setRelocateOpen] = useState(false);
  const [maintenanceOpen, setMaintenanceOpen] = useState(false);
  const [maintenanceAddToken, setMaintenanceAddToken] = useState(null);
  const [maintenanceFocusedLogId, setMaintenanceFocusedLogId] = useState(null);
  const [scheduleCameraId, setScheduleCameraId] = useState(null);
  const [sensorConfigOpen, setSensorConfigOpen] = useState(false);
  const [intrusionConfigOpen, setIntrusionConfigOpen] = useState(false);

  const openRelayBlade = (relayId, scheduleId, initialTab = 'config') => {
    setRelayBladeParams({ initialRelayId: relayId, initialScheduleId: scheduleId, initialTab });
    setIsRelayBladeOpen(true);
  };

  const closeRelayBlade = () => setIsRelayBladeOpen(false);

  const openCommands = () => setCommandsOpen(true);
  const closeCommands = () => setCommandsOpen(false);

  const openConfigureDisplay = () => setConfigureDisplayOpen(true);
  const closeConfigureDisplay = () => setConfigureDisplayOpen(false);

  const openRelocate = () => setRelocateOpen(true);
  const closeRelocate = () => setRelocateOpen(false);

  /**
   * Opens the maintenance blade, optionally focused on a specific log entry.
   *
   * @param {number|string|null} [logId] - log id to focus; omit for the full list view
   */
  const openMaintenance = (logId = null) => {
    setMaintenanceFocusedLogId(logId ?? null);
    setMaintenanceOpen(true);
  };
  const closeMaintenance = () => {
    setMaintenanceOpen(false);
    setMaintenanceFocusedLogId(null);
  };

  const handleAddMaintenance = () => {
    setMaintenanceFocusedLogId(null);
    setMaintenanceAddToken(token => (token == null ? 1 : token + 1));
    setMaintenanceOpen(true);
  };

  const openCameraSchedules = camera => setScheduleCameraId(camera.id);
  const closeCameraSchedules = () => setScheduleCameraId(null);

  const openSensorConfig = () => setSensorConfigOpen(true);
  const closeSensorConfig = () => setSensorConfigOpen(false);

  const openIntrusionConfig = () => setIntrusionConfigOpen(true);
  const closeIntrusionConfig = () => setIntrusionConfigOpen(false);

  return {
    // Relay blade
    isRelayBladeOpen,
    relayBladeParams,
    openRelayBlade,
    closeRelayBlade,
    // Commands blade
    commandsOpen,
    openCommands,
    closeCommands,
    // Configure display dialog
    configureDisplayOpen,
    openConfigureDisplay,
    closeConfigureDisplay,
    // Relocate structure/site dialog
    relocateOpen,
    openRelocate,
    closeRelocate,
    // Maintenance blade
    maintenanceOpen,
    maintenanceAddToken,
    maintenanceFocusedLogId,
    openMaintenance,
    closeMaintenance,
    handleAddMaintenance,
    // Camera schedules blade
    scheduleCameraId,
    openCameraSchedules,
    closeCameraSchedules,
    // Sensor config blade
    sensorConfigOpen,
    openSensorConfig,
    closeSensorConfig,
    // Intrusion config blade
    intrusionConfigOpen,
    openIntrusionConfig,
    closeIntrusionConfig,
  };
}
